import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { companyInfo } from '../mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success("Message Sent!", {
          description: `Your message has been sent to ${response.data.recipient_email}. We'll get back to you within 24 hours.`,
        });
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            subject: '',
            message: ''
          });
          setIsSubmitted(false);
        }, 3000);
      } else {
        toast.error("Error", {
          description: response.data.message,
        });
        setIsSubmitted(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Error", {
        description: "There was an error sending your message. Please try again or email us directly at " + companyInfo.email,
      });
      setIsSubmitted(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: [companyInfo.phone, companyInfo.businessHours.split(' ')[0] + '-' + companyInfo.businessHours.split(' ')[2]],
      link: `tel:${companyInfo.phone}`
    },
    {
      icon: Mail,
      title: 'Email',
      details: [companyInfo.email],
      link: `mailto:${companyInfo.email}`
    },
    {
      icon: MapPin,
      title: 'Location',
      details: [companyInfo.address],
      link: '#'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: [companyInfo.businessHours],
      link: '#'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-white to-brand-50 opacity-70"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-200 border-0 mb-4">
              <Mail size={14} className="mr-1" />
              Get In Touch
            </Badge>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Contact <span className="bg-gradient-to-r from-brand-600 to-brand-600 bg-clip-text text-transparent">Us</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Have questions about our products or services? Our team of experts is ready to help you find the perfect solution
            </p>
          </div>

          {/* Contact Info Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-200 group">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="text-white" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="text-sm text-gray-600">{detail}</p>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
                <p className="text-gray-600">
                  Fill out the form below and our team will respond within 24 hours
                </p>
              </div>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors outline-none"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors outline-none"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors outline-none"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors outline-none"
                          placeholder="Your Company"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors outline-none"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors outline-none resize-none"
                        placeholder="Tell us about your project or inquiry..."
                      ></textarea>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                      disabled={isSubmitted}
                    >
                      {isSubmitted ? (
                        <>
                          <CheckCircle className="mr-2" size={20} />
                          Message Sent!
                        </>
                      ) : (
                        <>
                          <Send className="mr-2" size={20} />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Additional Info & Image */}
            <div className="space-y-8">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1607980276897-c1aa5f8f9445?q=85"
                  alt="Vigyan Facility"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
              </div>

              <Card className="border-0 bg-gradient-to-br from-brand-600 to-brand-600 text-white shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-4">Why Choose Vigyan?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>14+ years of industry expertise and innovation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>500+ successfully installed processing plants</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>State-of-the-art CNC precision manufacturing</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Comprehensive after-sales support and service</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} />
                      <span>Next-gen automation with AI & IoT technology</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-0 bg-white shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Request a Quote</h3>
                  <p className="text-gray-600 mb-4">
                    Looking for a customized solution? Our engineering team will design a plant tailored to your specific requirements.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="mr-2 text-brand-600" size={16} />
                      Free consultation and site survey
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="mr-2 text-brand-600" size={16} />
                      Detailed technical specifications
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="mr-2 text-brand-600" size={16} />
                      Competitive pricing and flexible terms
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;